import React, { useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { useSelector } from 'react-redux'
import numeral from 'numeral'

import Table from '../../../Invoices/Table'
import EmployeeSalesModal from '../EmployeeSalesModal'

import styles from './styles.module.scss'

import { getCurrency } from '../../../../helpers/currencies'
import getUniqueArrayByKey from '../../../../helpers/getUniqueArrayByKey'

const columnHelper = createColumnHelper()

const formatNumber = (value, currency) => {
  return `${numeral(value).format('0,0.[00]')} ${currency}`
}

const getBonusAmount = (target, sales) => {
  switch (target.bonus_type) {
    case 'fixed':
      return target.bonus_amount
    case 'percent_from_sales':
      return (sales * target.bonus_amount) / 100
    case 'percent_above_target':
      return sales > target.sales_target_amount ? ((sales - target.sales_target_amount) * target.bonus_amount) / 100 : 0
    default:
      return 0
  }
}

const getBonusTitle = (bonusType) => {
  switch (bonusType) {
    case 'fixed':
      return 'Fixed'
    case 'percent_from_sales':
      return 'Percent from sales'
    case 'percent_above_target':
      return 'Percent above target'
    default:
      return '-'
  }
}

const SalesByEmployee = ({sales, companyEmployees, employeesTargets}) => {
  const { t } = useTranslation()

  const companyInfo = useSelector(state => state.company.companyInfo)
  const currency = getCurrency(companyInfo?.currency)

  const employeeSalesModalRef = useRef(null)

  const data = useMemo(() => {
    const employeesMap = companyEmployees.reduce((acc, employee) => {
      return {
        ...acc,
        [employee.iiko_user_id]: employee,
      }
    }, {})
    const employeesTargetsMap = employeesTargets.reduce((acc, target) => {
      return {
        ...acc,
        [target.employee.iiko_user_id]: target,
      }
    }, {})
    const salesByEmployee = sales.reduce((acc, sale) => {
      const employeeId = sale['WaiterName.ID'] || 'Uncategorized'
      return {
        ...acc,
        [employeeId]: acc[employeeId] ? [...acc[employeeId], sale] : [sale],
      }
    }, {})
    return Object.entries(salesByEmployee).map(([employeeId, sales]) => {
      const employee = employeesMap[employeeId] || {name: 'Uncategorized', surname: ''}
      const billsCount = getUniqueArrayByKey(sales, 'OrderNum').length
      const employeeTarget = employeesTargetsMap[employeeId]?.active ? employeesTargetsMap[employeeId] : null
      const salesAmount = sales.reduce((acc, sale) => acc + (sale.DishDiscountSumInt || 0), 0)
      return {
        name: `${employee.name} ${employee.surname}`,
        salesAmount: salesAmount,
        bills: billsCount,
        avgBill: billsCount > 0 ? sales.reduce((acc, sale) => acc + (sale.DishDiscountSumInt || 0), 0) / billsCount : 0,
        salesTarget: employeeTarget && employeeTarget.sales_target ? employeeTarget.sales_target_amount : 0,
        bonusType: employeeTarget && employeeTarget.bonus ? employeeTarget.bonus_type : '-',
        bonusAmount: employeeTarget && employeeTarget.bonus ? getBonusAmount(employeeTarget, salesAmount) : 0,
        employeeCost: employeesMap[employeeId] && (employee.work_duration / 60) * (employee.profitability?.cost || 0),
        transactions: sales,
      }
    })
  }, [sales, companyEmployees, employeesTargets])

  const columns = useMemo(() => ([
    columnHelper.accessor('name', {
      id: 'name',
      header: t('Employee'),
      enableSorting: false,
      cell: ({ getValue }) => {
        return getValue()
      }
    }),
    columnHelper.accessor('salesAmount', {
      id: 'salesAmount',
      enableSorting: false,
      cell: ({ getValue }) => formatNumber(getValue(), currency.symbol),
      header: t('Sales'),
    }),
    columnHelper.accessor('bills', {
      id: 'bills',
      enableSorting: false,
      cell: ({ getValue }) => getValue(),
      header: t('Bills'),
    }),
    columnHelper.accessor('avgBill', {
      id: 'avgBill',
      enableSorting: false,
      cell: ({ getValue }) => formatNumber(getValue(), currency.symbol),
      header: t('Avg. check'),
    }),
    columnHelper.accessor('salesTarget', {
      id: 'salesTarget',
      enableSorting: false,
      cell: ({ getValue }) => formatNumber(getValue(), currency.symbol),
      header: t('Sales target'),
    }),
    columnHelper.accessor('bonusType', {
      id: 'bonusType',
      enableSorting: false,
      cell: ({ getValue }) => t(getBonusTitle(getValue())),
      header: t('Bonus type'),
    }),
    columnHelper.accessor('bonusAmount', {
      id: 'bonusAmount',
      enableSorting: false,
      cell: ({ getValue }) => formatNumber(getValue(), currency.symbol),
      header: t('Bonus amount'),
    }),
    columnHelper.display({
      id: 'status',
      enableSorting: false,
      cell: ({ row }) => {
        return (
          <span className={styles.detailsButton} onClick={() => {
            employeeSalesModalRef.current.open(row.original)
          }}>{t('Open')}</span>
        )
      },
      header: t('Details')  
    })
    
  ]), [t, currency.symbol])
  
  const table = useReactTable({
    data: data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.title}>{t('Sales by employee')}</div>
      </div>
      <Table
        table={table}
        columns={columns}
        wrapperStyle={{flex: 1}}
        headerClassName={styles.tableHeader} />
      <EmployeeSalesModal ref={employeeSalesModalRef} />
    </div>
  )
}

export default SalesByEmployee